import API_CONFIG from "../../config/api";

const base = API_CONFIG.BASE_URL;
const headers = (companyId = null) => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  ...(companyId ? { "X-Company-Id": String(companyId) } : {}),
});

const parse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `Erreur ${response.status}`);
  return data;
};

const queryString = (params) => new URLSearchParams(
  Object.entries(params).filter(([, value]) => value !== "" && value !== null && value !== undefined)
).toString();

const scopedParams = (params, scope) => ({
  ...params,
  ...(scope === "all" ? { scope: "all" } : {}),
});

export async function getAuditDashboard(period = 7, companyId, options = {}) {
  const response = await parse(await fetch(
    `${base}/api/audit/dashboard?${queryString(scopedParams({ period,
      ...(options.warehouseId ? { warehouseId: options.warehouseId } : {}),
      ...(options.fresh !== undefined ? { fresh: options.fresh } : {}),
      ...(options.includeAuth !== undefined ? { includeAuth: options.includeAuth } : {}),
    }, options.scope))}`,
    { headers: headers(companyId), signal: options.signal, cache: 'no-store' }
  ));
  if (response.success !== true || response.data?.global_stats?.total_logs == null ||
      !Number.isFinite(Number(response.data.global_stats.total_logs))) {
    throw new Error('Le tableau de bord est indisponible. Réessayez.');
  }
  return normalizeAuditDashboard(response.data);
}

const auditNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
export const normalizeAuditDashboard = (data) => ({
  ...data,
  global_stats: Object.fromEntries(['total_logs', 'active_users', 'modules_used', 'success_count', 'failure_count', 'partial_count']
    .map((key) => [key, auditNumber(data.global_stats?.[key])])),
  top_modules: (data.top_modules || []).map((item) => ({ ...item, total_actions: auditNumber(item.total_actions), success_rate: auditNumber(item.success_rate) })),
  top_users: (data.top_users || []).map((item) => ({ ...item, total_actions: auditNumber(item.total_actions), avg_success_rate: auditNumber(item.avg_success_rate) })),
  activity_timeline: (data.activity_timeline || []).map((item) => ({ ...item,
    ...Object.fromEntries(['total_actions', 'success_count', 'failure_count', 'partial_count'].map((key) => [key, auditNumber(item[key])])),
  })),
  critical_actions: data.critical_actions || [],
});

export const auditDashboardLogUrl = (data, { scope, warehouseId, includeAuth = true, module, criticalOnly, status, logId } = {}) => {
  const sqlDate = (value) => value ? value.slice(0, 23).replace('T', ' ') : '';
  return `/audit/logs?${queryString(scopedParams({
    module, warehouseId, includeAuth, criticalOnly, status, logId,
    startDate: sqlDate(data?.range?.start), endDate: sqlDate(data?.range?.end),
  }, scope))}`;
};

export async function listAuditLogs(params, companyId, options = {}) {
  return parse(await fetch(`${base}/api/audit/logs?${queryString(params)}`, {
    headers: headers(companyId),
    signal: options.signal,
  }));
}

export async function listAuditConnections(params, companyId, options = {}) {
  return parse(await fetch(
    `${base}/api/audit/auth/history?${queryString(scopedParams(params, options.scope))}`,
    { headers: headers(companyId), signal: options.signal }
  ));
}

export async function listAuditSessions(windowMinutes = 15, companyId, options = {}) {
  return parse(await fetch(
    `${base}/api/audit/auth/active?${queryString(scopedParams({ windowMinutes }, options.scope))}`,
    { headers: headers(companyId), signal: options.signal }
  ));
}

export const parseAuditDetails = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return { description: String(value) }; }
};

export const auditDate = (value, seconds = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) });
};

export const MODULE_LABELS = {
  produits: "Produits", ventes: "Ventes", achats: "Achats", rh: "Ressources humaines",
  depenses_commercial: "Dépenses · Commercial", depenses_logistics: "Dépenses · Logistique",
  depenses_hotellerie: "Dépenses · Hôtellerie", logistics: "Logistique", users: "Utilisateurs",
  utilisateurs: "Utilisateurs", companies: "Entreprises", entreprises: "Entreprises",
  ecommerce: "E-commerce", warehouse: "Établissements", warehouses: "Établissements", stock: "Stock", payments: "Paiements",
  paiements: "Paiements", reports: "Rapports", settings: "Paramètres", auth: "Authentification",
  production: 'Production',
};

export const moduleLabel = (value) => MODULE_LABELS[value] || value || "Système";
export const statusMeta = (status) => ({
  success: ["Succès", "ok"], failure: ["Échec", "danger"], partial: ["Partiel", "warn"],
}[status] || [status || "Inconnu", "off"]);
